import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarClock } from 'lucide-react';
import { T, springs } from '../../lib/tokens';
import { deferTask } from '../../lib/taskActions';

// ─── Defer menu ──────────────────────────────────────────────────────────────
// One-click snooze, reused on every task surface. Renders as a small icon (or
// a labelled chip with `label`); opens a popover with Tomorrow / Next week /
// Pick a date. Calls onDeferred(until) after the write so the caller can
// refetch + re-run the scheduler.

export const DeferMenu = ({ task, onDeferred, label, align = 'right' }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const defer = async (when) => {
    if (busy) return;
    setBusy(true);
    const until = await deferTask(task, when);
    setBusy(false);
    setOpen(false);
    onDeferred && onDeferred(until);
  };

  const itemStyle = {
    display: 'block', width: '100%', textAlign: 'left', padding: '7px 12px',
    border: 'none', background: 'none', cursor: 'pointer', fontSize: 12,
    color: T.text, fontFamily: 'inherit', borderRadius: 6,
  };

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        title="Defer"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        style={label ? {
          display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px',
          borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
          fontFamily: 'inherit', color: T.textMid,
          background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)',
        } : {
          padding: 4, border: 'none', background: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center',
        }}
      >
        <CalendarClock size={label ? 12 : 13} color={T.textMuted} />
        {label && label}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.96 }}
            transition={springs.snappy}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', top: '100%', [align]: 0, marginTop: 6, zIndex: 60,
              width: 168, padding: 6,
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10, boxShadow: T.shadowLg,
            }}
          >
            <div style={{ fontSize: 9, fontWeight: 700, color: T.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 12px 6px' }}>
              Defer to
            </div>
            <button style={itemStyle} disabled={busy}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
              onClick={() => defer('tomorrow')}>
              Tomorrow
            </button>
            <button style={itemStyle} disabled={busy}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
              onClick={() => defer('nextweek')}>
              Next week
            </button>
            <div style={{ padding: '6px 12px 4px', borderTop: '1px solid rgba(0,0,0,0.05)', marginTop: 4 }}>
              <input
                type="date"
                min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                onChange={(e) => { if (e.target.value) defer(new Date(e.target.value + 'T09:00:00')); }}
                style={{
                  width: '100%', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6,
                  padding: '4px 6px', fontSize: 11, color: T.textMid,
                  fontFamily: 'inherit', background: '#fff',
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
